import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Tooltip,
  Stack,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccountBalanceWallet,
  Visibility,
  VisibilityOff,
  PhotoSizeSelectActual,
  AccountBalance,
  Link as LinkMuiIcon,
  QrCode2,
  KeyboardArrowRight,
  History,
  Sync,
  Refresh,
  Smartphone,
  SettingsOutlined,
  ContentCopy,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { SOCKET_URL } from '../../config/api';
import SePayTransactionsTable from '../shared/SePayTransactionsTable';
import PhotosSegmentedControl from '../shared/PhotosSegmentedControl';
import { useI18n } from '../../i18n/useI18n';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';

type SePayUserTab = 'config' | 'price' | 'device';

/** Hiển thị placeholder khi đã lưu mã nhưng chưa nhập đúng mật khẩu xem được */
const MASK_DISPLAY = `${'\u2022'.repeat(24)}`;

const REVEAL_MS = 5 * 60 * 1000;

const MONEY_FMT = new Intl.NumberFormat('vi-VN');
const TOAST_MS = 2000;

/** Ô giá VND: chỉ lấy chữ số (tối đa 9) để gửi API */
function digitsOnlyVndPrice(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 9);
}

/** Hiển thị phần nghìn kiểu Việt (vd. 3000 → 3.000) */
function formatVndPriceDisplay(digits: string): string {
  if (!digits) return '';
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n)) return '';
  return MONEY_FMT.format(n);
}

type SePayKeyField = 'secretKey';

const EMPTY_SEPAY_FIELD_VIS = (): Record<SePayKeyField, boolean> => ({
  secretKey: false,
});

function toastSePayUpdate(txn: api.SePayTransactionRow): void {
  const oc = txn.orderInvoiceNumber;
  const id = `sepay-done-${oc}-${txn.status}`;
  if (txn.status === 'CAPTURED') {
    toast.success(`Thanh toán thành công — đơn ${oc}`, { toastId: id, autoClose: TOAST_MS });
    return;
  }
  if (txn.status !== 'PENDING') {
    toast.info(`Đơn ${oc}: ${txn.status}`, { toastId: id, autoClose: TOAST_MS });
  }
}

const PaymentPayOs: React.FC = () => {
  const { pages: t } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  /** Cập nhật mỗi giây để khóa lại đúng lúc hết 5 phút */
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [amountStr, setAmountStr] = useState('2000');
  const [description, setDescription] = useState('Demo thanh toán M-Photo Pro');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [revealDialogOpen, setRevealDialogOpen] = useState(false);
  const [revealPw, setRevealPw] = useState('');

  // SePay states
  const [sePayConfigured, setSePayConfigured] = useState<boolean | null>(null);
  const [sePaySecretKey, setSePaySecretKey] = useState('');
  const [sePayRevealExpiresAt, setSePayRevealExpiresAt] = useState<number | null>(null);
  const [sePayFieldPlainVisible, setSePayFieldPlainVisible] = useState(EMPTY_SEPAY_FIELD_VIS);
  const [sePayPendingPwField, setSePayPendingPwField] = useState<SePayKeyField | null>(null);
  const [sePayRows, setSePayRows] = useState<api.SePayTransactionRow[]>([]);
  const [sePayTxLoading, setSePayTxLoading] = useState(false);
  const [sePayQrOpen, setSePayQrOpen] = useState(false);
  const [sePayQrData, setSePayQrData] = useState<{
    qrUrl: string;
    orderInvoiceNumber: string;
    amount: number;
    content: string;
    accountNumber: string;
    accountName: string;
    bankShortName: string;
  } | null>(null);
  const [sePayBankInfo, setSePayBankInfo] = useState<api.SePayBankInfo | null>(null);
  const [sePayWebhookUrl, setSePayWebhookUrl] = useState('');
  const [sePayInvoicePrefix, setSePayInvoicePrefix] = useState('');
  const [sePayPrice4x6Vnd, setSePayPrice4x6Vnd] = useState<number | null>(null);
  const [price4x6Input, setPrice4x6Input] = useState('');
  const [price4x6Busy, setPrice4x6Busy] = useState(false);
  const [prefixInput, setPrefixInput] = useState('');
  const [prefixBusy, setPrefixBusy] = useState(false);
  const [sePayBankDialogOpen, setSePayBankDialogOpen] = useState(false);
  const [sePayBankForm, setSePayBankForm] = useState({ bankShortName: '', accountNumber: '', accountName: '' });
  const [sePayAccountsList, setSePayAccountsList] = useState<api.SePayBankAccountItem[]>([]);
  const [sePayVAList, setSePayVAList] = useState<api.SePayVirtualAccountItem[]>([]);
  const [sePaySelectedBankId, setSePaySelectedBankId] = useState('');
  const [sePaySelectedVA, setSePaySelectedVA] = useState('');
  const [sePayAccountsLoading, setSePayAccountsLoading] = useState(false);

  const [channelAccountFilter, setChannelAccountFilter] = useState<string>('ALL'); // thực tế: lọc theo ngân hàng nhận
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'PAID'>('ALL');
  const [createdFrom, setCreatedFrom] = useState<string>(''); // YYYY-MM-DD
  const [createdTo, setCreatedTo] = useState<string>(''); // YYYY-MM-DD
  const [paidFrom, setPaidFrom] = useState<string>(''); // YYYY-MM-DD
  const [paidTo, setPaidTo] = useState<string>(''); // YYYY-MM-DD
  const [showAdvanced, setShowAdvanced] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const [sePayMainTab, setSePayMainTab] = useState<SePayUserTab>('config');
  const [sePayMobileLiteRequireCheckout, setSePayMobileLiteRequireCheckout] = useState(true);
  const [sePayMobileProRequireCheckout, setSePayMobileProRequireCheckout] = useState(true);
  const [sePayDeviceFlagsBusy, setSePayDeviceFlagsBusy] = useState(false);

  const loadSePaySettings = async () => {
    const s = await api.getSePaySettings();
    setSePayConfigured(s.configured);
    if (s.bankInfo) setSePayBankInfo(s.bankInfo);
    setSePayInvoicePrefix(s.invoicePrefix || '');
    setPrefixInput(s.invoicePrefix || '');
    const p46 = s.price4x6Vnd;
    setSePayPrice4x6Vnd(p46 ?? null);
    setPrice4x6Input(p46 != null ? formatVndPriceDisplay(String(p46)) : '');
    setSePayMobileLiteRequireCheckout(s.sePayMobileLiteRequireCheckout ?? true);
    setSePayMobileProRequireCheckout(s.sePayMobileProRequireCheckout ?? true);
    setSePaySecretKey('');
    try {
      const w = await api.getSePayWebhookUrl();
      setSePayWebhookUrl(w.webhookUrl);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!sePayRevealExpiresAt) return;
    if (nowTick < sePayRevealExpiresAt) return;
    setSePayRevealExpiresAt(null);
    setSePayFieldPlainVisible(EMPTY_SEPAY_FIELD_VIS());
    setSePaySecretKey('');
    toast.info('Đã ẩn mã SePay (hết 5 phút).', { autoClose: 3000 });
  }, [sePayRevealExpiresAt, nowTick]);

  const loadSePayTx = useCallback(async () => {
    try {
      setSePayTxLoading(true);
      const cleanup = await api.cleanupStaleSePayTransactions();
      if (cleanup.deletedCount > 0) {
        toast.info(
          `Đã xóa ${cleanup.deletedCount} đơn chờ thanh toán quá 1 ngày.`,
          { autoClose: 4000 },
        );
      }
      const list = await api.getSePayTransactions();
      setSePayRows(list);
    } catch {
      /* ignore list errors */
    } finally {
      setSePayTxLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadSePaySettings();
        await loadSePayTx();
      } catch {
        toast.error('Không tải được cấu hình SePay.');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadSePayTx]);

  /** Socket — server đẩy khi webhook SePay hoặc đồng bộ đổi trạng thái */
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1500,
      path: '/socket.io',
    });
    socketRef.current = socket;

    const join = () => {
      socket.emit('join-user-room');
    };
    socket.on('connect', join);

    const onSePayUpdated = (payload: {
      transaction?: api.SePayTransactionRow;
      source?: string;
      orderInvoiceNumber?: string;
    }) => {
      const txn = payload?.transaction;
      if (txn && (txn.orderInvoiceNumber ?? payload.orderInvoiceNumber)) {
        toastSePayUpdate(txn as api.SePayTransactionRow);
        if (txn.status === 'CAPTURED') {
          setSePayQrData(prev => {
            if (prev && prev.orderInvoiceNumber === txn.orderInvoiceNumber) {
              setSePayQrOpen(false);
            }
            return prev;
          });
        }
      }
      void loadSePayTx();
    };

    socket.on('sepay:transaction-updated', onSePayUpdated);

    join();

    return () => {
      socket.emit('leave-user-room');
      socket.off('sepay:transaction-updated', onSePayUpdated);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [loadSePayTx]);

  useEffect(() => {
    const onSepayDone = (ev: MessageEvent) => {
      const d = ev.data;
      if (!d || d.type !== 'M_PHOTO_SEPAY_DONE') return;
      if (d.status === 'success') {
        toast.info('SePay: thanh toán thành công — đang cập nhật danh sách.', { autoClose: 3200 });
      } else if (d.status === 'cancel') {
        toast.info('SePay: đã hủy.', { autoClose: 2800 });
      } else if (d.status === 'error') {
        toast.warning('SePay: thanh toán lỗi hoặc bị từ chối.', { autoClose: 3800 });
      }
      void loadSePayTx();
    };
    window.addEventListener('message', onSepayDone);
    return () => window.removeEventListener('message', onSepayDone);
  }, [loadSePayTx]);


  // SePay functions
  const sePayCredentialsUnlocked = Boolean(sePayRevealExpiresAt != null && nowTick < sePayRevealExpiresAt);

  const sePayDotsFor = useCallback(
    (f: SePayKeyField) => {
      if (f !== 'secretKey') return false;
      if (!sePayConfigured) return false;
      if (!sePayCredentialsUnlocked) return true;
      return !sePayFieldPlainVisible.secretKey;
    },
    [sePayConfigured, sePayCredentialsUnlocked, sePayFieldPlainVisible],
  );

  const openSePayRevealForField = useCallback((field: SePayKeyField) => {
    setSePayPendingPwField(field);
    setRevealPw('');
    setRevealDialogOpen(true);
  }, []);

  const onSePayEyeFieldClick = useCallback(
    (field: SePayKeyField) => {
      if (field !== 'secretKey' || !sePayConfigured) return;
      if (!sePayCredentialsUnlocked) {
        openSePayRevealForField(field);
        return;
      }
      setSePayFieldPlainVisible((prev) => ({ ...prev, secretKey: !prev.secretKey }));
    },
    [sePayConfigured, sePayCredentialsUnlocked, openSePayRevealForField],
  );

  const confirmSePayRevealKeys = async () => {
    if (!revealPw.trim()) {
      toast.error('Nhập mật khẩu tài khoản.');
      return;
    }
    try {
      setLoading(true);
      const d = await api.revealSePaySettings(revealPw);
      setSePaySecretKey(d.secretKey);
      setSePayRevealExpiresAt(Date.now() + REVEAL_MS);
      setSePayFieldPlainVisible({ secretKey: true });
      setSePayPendingPwField(null);
      setRevealDialogOpen(false);
      setRevealPw('');
      toast.success('Đã mở phiên 5 phút — hiện API Token.', { autoClose: 2800 });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Mật khẩu không đúng');
    } finally {
      setLoading(false);
    }
  };

  const sePayFieldEyeAdornment = (field: SePayKeyField) => {
    if (field !== 'secretKey' || !sePayConfigured) return undefined;
    const needPw = !sePayCredentialsUnlocked;
    const plain = sePayFieldPlainVisible.secretKey;
    const label = 'API Token';
    const tip = needPw
      ? `Nhập mật khẩu tài khoản để xem ${label}`
      : plain
        ? 'Ẩn ô này (vẫn trong phiên, không mật khẩu)'
        : 'Hiện ô này (trong phiên không cần mật khẩu)';
    const EyeIconCmp = sePayCredentialsUnlocked && plain ? VisibilityOff : Visibility;
    return (
      <InputAdornment position="end">
        <Tooltip title={tip}>
          <span>
            <IconButton
              edge="end"
              aria-label={`SePay ${field}`}
              type="button"
              onClick={() => onSePayEyeFieldClick(field)}
              tabIndex={-1}
            >
              <EyeIconCmp fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </InputAdornment>
    );
  };

  const handleSePaySaveKeys = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (sePayConfigured && !sePayCredentialsUnlocked) {
        toast.warning('Bấm con mắt để nhập mật khẩu mở phiên 5 phút, sau đó mới lưu được.');
        return;
      }
      const s = sePaySecretKey.trim();
      if (!s) {
        toast.error('Cần nhập API Token.');
        return;
      }
      setLoading(true);
      const result = await api.putSePaySettings({
        merchantId: '',
        secretKey: s,
        sePayUsePaymentGateway: false,
        sePayPgEnv: 'production',
      });
      if (result.bankInfo) {
        setSePayBankInfo(result.bankInfo);
        toast.success(`Đã lưu API Token — tài khoản: ${result.bankInfo.bankShortName} ${result.bankInfo.accountNumber}`);
      } else {
        toast.success('Đã lưu API Token SePay.');
        if ((result as any).bankFetchError) {
          toast.warning('Chưa lấy được thông tin ngân hàng tự động — dùng nút "Nhập tay thông tin ngân hàng".');
        }
      }
      setSePayRevealExpiresAt(null);
      setSePayFieldPlainVisible(EMPTY_SEPAY_FIELD_VIS());
      setSePaySecretKey('');
      await loadSePaySettings();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Lưu thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrice4x6 = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = digitsOnlyVndPrice(price4x6Input);
    let value: number | null;
    if (!digits) {
      value = null;
    } else {
      const n = parseInt(digits, 10);
      if (!Number.isFinite(n) || n < 0 || n > 999_999_999) {
        toast.error('Giá không hợp lệ (0 – 999.999.999 VND).');
        return;
      }
      value = n;
    }
    try {
      setPrice4x6Busy(true);
      const r = await api.putSePayPrice4x6(value);
      setSePayPrice4x6Vnd(r.price4x6Vnd);
      setPrice4x6Input(r.price4x6Vnd != null ? formatVndPriceDisplay(String(r.price4x6Vnd)) : '');
      toast.success(r.message);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Không lưu được giá.');
    } finally {
      setPrice4x6Busy(false);
    }
  };

  const saveSePayMobileCheckoutFlag = async (patch: { sePayMobileLiteRequireCheckout?: boolean; sePayMobileProRequireCheckout?: boolean }) => {
    if (sePayDeviceFlagsBusy) return;
    try {
      setSePayDeviceFlagsBusy(true);
      const r = await api.putSePayMobileCheckoutFlags(patch);
      setSePayMobileLiteRequireCheckout(r.sePayMobileLiteRequireCheckout);
      setSePayMobileProRequireCheckout(r.sePayMobileProRequireCheckout);
      toast.success(r.message || 'Đã cập nhật.', { autoClose: TOAST_MS });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Không lưu được cấu hình thiết bị.');
    } finally {
      setSePayDeviceFlagsBusy(false);
    }
  };

  const handleSePayCreateCheckout = async () => {
    const amount = Number(amountStr.replace(/[^\d]/g, ''));
    if (!Number.isFinite(amount) || amount < 1000) {
      toast.error('Số tiền tối thiểu 1000 VND.');
      return;
    }
    try {
      setLoading(true);
      const out = await api.createSePayCheckout({
        amount,
        description: description.trim(),
      });
      if (out.checkoutUrl) {
        setSePayQrData({
          qrUrl: out.checkoutUrl,
          orderInvoiceNumber: out.orderInvoiceNumber,
          amount,
          content: out.formFields?.content || out.orderInvoiceNumber,
          accountNumber: out.formFields?.accountNumber || '',
          accountName: out.formFields?.accountName || '',
          bankShortName: out.formFields?.bankShortName || out.formFields?.bankCode || '',
        });
        setSePayQrOpen(true);
        toast.success('Đã tạo QR thanh toán SePay — quét mã để chuyển khoản.');
      } else {
        toast.warning('Không nhận được thông tin checkout.');
      }
      await loadSePayTx();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Tạo checkout thất bại');
    } finally {
      setLoading(false);
    }
  };

  const ymdToMs = (ymd: string) => {
    if (!ymd) return null;
    const d = new Date(`${ymd}T00:00:00`);
    const t = d.getTime();
    return Number.isFinite(t) ? t : null;
  };

  const withinRange = (ts: number | null, fromYmd: string, toYmd: string) => {
    if (ts == null) return false;
    const from = ymdToMs(fromYmd);
    const to = ymdToMs(toYmd);
    if (from != null && ts < from) return false;
    if (to != null) {
      const end = to + 24 * 60 * 60 * 1000 - 1;
      if (ts > end) return false;
    }
    return true;
  };



  const sectionCard = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
  } as const;

  const iconBox = (color: 'primary' | 'secondary' = 'primary') => ({
    width: 40,
    height: 40,
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    bgcolor: `${color}.main`,
    color: color === 'primary' ? '#fff' : 'secondary.contrastText',
  });

  const pillBtn = {
    textTransform: 'none' as const,
    fontWeight: 800,
    borderRadius: 999,
    boxShadow: 'none',
  };

  return (
    <UserPageShell maxWidth="md" enter>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2.25 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.18),
            background: `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, #fff 55%)`,
            ...userCardEnterSx(0),
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: { xs: 42, sm: 48 },
                height: { xs: 42, sm: 48 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.28)}`,
              }}
            >
              <AccountBalanceWallet sx={{ fontSize: { xs: 22, sm: 26 } }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                fontWeight={800}
                sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, letterSpacing: '-0.02em', lineHeight: 1.25 }}
              >
                {t.sePay.title}
              </Typography>
              {!isMobile && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {t.sePay.subtitle}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              label={sePayConfigured ? 'Đã cấu hình' : 'Chưa cấu hình'}
              color={sePayConfigured ? 'success' : 'default'}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
          </Stack>
        </Paper>

        <PhotosSegmentedControl
          value={sePayMainTab}
          onChange={setSePayMainTab}
          fullWidth
          size="sm"
          options={[
            { value: 'config', label: t.sePay.tabs.config, icon: <SettingsOutlined fontSize="inherit" /> },
            { value: 'price', label: isMobile ? t.sePay.tabs.price : t.sePay.tabs.price, icon: <PhotoSizeSelectActual fontSize="inherit" /> },
            { value: 'device', label: t.sePay.tabs.device, icon: <Smartphone fontSize="inherit" /> },
          ]}
        />

        {sePayMainTab === 'config' && (
          <Paper elevation={0} sx={{ ...sectionCard, ...userCardEnterSx(1) }}>
            {sePayWebhookUrl && (
              <Box
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <LinkMuiIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1 }}>
                    {t.sePay.webhookTitle}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(sePayWebhookUrl);
                      toast.success('Đã sao chép webhook URL.', { autoClose: 1500 });
                    }}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.75, lineHeight: 1.4 }}
                >
                  {t.sePay.webhookHint}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all', fontSize: '0.78rem' }}
                >
                  {sePayWebhookUrl}
                </Typography>
              </Box>
            )}

            <Box
              onClick={() => {
                setSePayBankForm({
                  bankShortName: sePayBankInfo?.bankShortName || '',
                  accountNumber: sePayBankInfo?.accountNumber || '',
                  accountName: sePayBankInfo?.accountName || '',
                });
                setSePayBankDialogOpen(true);
              }}
              sx={{
                p: 1.5,
                mb: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: sePayBankInfo ? alpha('#2e7d32', 0.35) : 'divider',
                bgcolor: sePayBankInfo ? alpha('#2e7d32', 0.06) : alpha(theme.palette.primary.main, 0.03),
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <AccountBalance sx={{ fontSize: 22, color: sePayBankInfo ? 'success.main' : 'primary.main', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {t.sePay.receivingAccount}
                </Typography>
                {sePayBankInfo ? (
                  <>
                    <Typography variant="body2" fontWeight={800} noWrap>
                      {sePayBankInfo.bankShortName}{' '}
                      <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
                        {sePayBankInfo.accountNumber}
                      </Box>
                    </Typography>
                    {sePayBankInfo.accountName ? (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {sePayBankInfo.accountName}
                      </Typography>
                    ) : null}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {t.sePay.tapToConfig}
                  </Typography>
                )}
              </Box>
              <KeyboardArrowRight sx={{ color: 'text.disabled', flexShrink: 0 }} />
            </Box>

            <TextField
              fullWidth
              size="small"
              label={t.sePay.apiTokenLabel}
              placeholder="Lấy tại: SePay → Cài đặt công ty → API Access"
              value={sePayDotsFor('secretKey') ? MASK_DISPLAY : sePaySecretKey}
              onChange={(e) => setSePaySecretKey(e.target.value)}
              autoComplete="off"
              type={sePayDotsFor('secretKey') ? 'text' : !sePayConfigured ? 'password' : sePayCredentialsUnlocked ? 'text' : 'password'}
              sx={{ mb: 1.5 }}
              helperText={!sePayConfigured ? 'Token từ API Access (SePay). Không phải mật khẩu đăng nhập.' : undefined}
              InputProps={{
                readOnly: sePayDotsFor('secretKey'),
                ...(sePayFieldEyeAdornment('secretKey') ? { endAdornment: sePayFieldEyeAdornment('secretKey') } : {}),
              }}
            />

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.16),
                background: `linear-gradient(160deg, #fff 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: '0.04em', display: 'block', mb: 1.25 }}
              >
                CẬP NHẬT
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, lineHeight: 1.5 }}>
                Cách lấy API Token: Đăng nhập{' '}
                <a href="https://my.sepay.vn/companyapi" target="_blank" rel="noopener noreferrer">
                  my.sepay.vn → API Access
                </a>{' '}
                → + Thêm API → Copy token.
              </Typography>
              <Stack spacing={1.25} component="form" onSubmit={handleSePaySaveKeys}>
                <Button
                  type="submit"
                  fullWidth={isMobile}
                  variant="contained"
                  disabled={loading}
                  sx={{ ...pillBtn, alignSelf: { xs: 'stretch', sm: 'flex-start' }, px: 2.5 }}
                >
                  {loading ? <CircularProgress size={16} color="inherit" /> : t.common.save}
                </Button>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1.5 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Prefix mã đơn"
                  placeholder={sePayInvoicePrefix || 'MPRO'}
                  value={prefixInput}
                  onChange={(e) =>
                    setPrefixInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))
                  }
                  inputProps={{ maxLength: 8 }}
                  helperText={`Hiện tại: ${sePayInvoicePrefix || 'MPRO'} · VD: ${prefixInput || sePayInvoicePrefix || 'MPRO'}A1B2C3D4`}
                />
                <Button
                  variant="contained"
                  disabled={prefixBusy}
                  onClick={async () => {
                    if (prefixBusy) return;
                    setPrefixBusy(true);
                    try {
                      const r = await api.updateSePayInvoicePrefix(prefixInput.trim());
                      setSePayInvoicePrefix(r.invoicePrefix);
                      setPrefixInput(r.invoicePrefix);
                      toast.success(r.message, { autoClose: TOAST_MS });
                    } catch (err: unknown) {
                      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
                      toast.error(msg || 'Không lưu được prefix.');
                    } finally {
                      setPrefixBusy(false);
                    }
                  }}
                  sx={{ ...pillBtn, flexShrink: 0, px: 2.5, alignSelf: { sm: 'flex-start' }, mt: { sm: 0.25 } }}
                >
                  {prefixBusy ? <CircularProgress size={16} color="inherit" /> : 'Lưu prefix'}
                </Button>
              </Stack>
            </Box>
          </Paper>
        )}

        {sePayMainTab === 'price' && (
          <Paper elevation={0} sx={{ ...sectionCard, ...userCardEnterSx(1) }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.75 }}>
              <Box sx={iconBox()}>
                <PhotoSizeSelectActual sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                  {t.sePay.tabs.price}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Để trống + Lưu để xóa giá
                </Typography>
              </Box>
            </Stack>
            <Stack
              component="form"
              onSubmit={handleSavePrice4x6}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Box
                sx={{
                  width: { xs: 96, sm: 110 },
                  aspectRatio: '2 / 3',
                  mx: { xs: 'auto', sm: 0 },
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  background: 'linear-gradient(160deg, #f8fafc 0%, #e2e8f0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography fontWeight={800} color="primary.dark">
                  4×6
                </Typography>
              </Box>
              <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t.sePay.priceLabel}
                  value={price4x6Input}
                  onChange={(e) => {
                    const d = digitsOnlyVndPrice(e.target.value);
                    setPrice4x6Input(d === '' ? '' : formatVndPriceDisplay(d));
                  }}
                  placeholder="VD: 20.000"
                  disabled={price4x6Busy}
                  inputProps={{ inputMode: 'numeric' }}
                  helperText={
                    sePayPrice4x6Vnd != null
                      ? `Đang lưu: ${MONEY_FMT.format(sePayPrice4x6Vnd)} đ`
                      : 'Chưa lưu giá'
                  }
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    type="submit"
                    fullWidth={isMobile}
                    variant="contained"
                    disabled={price4x6Busy}
                    sx={{ ...pillBtn, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                  >
                    {price4x6Busy ? <CircularProgress size={16} color="inherit" /> : t.sePay.savePrice}
                  </Button>
                  {price4x6Input !== '' && (
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={price4x6Busy}
                      onClick={() => setPrice4x6Input('')}
                      sx={{ ...pillBtn, fontWeight: 700 }}
                    >
                      {t.sePay.clearInput}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        )}

        {sePayMainTab === 'device' && (
          <Paper elevation={0} sx={{ ...sectionCard, ...userCardEnterSx(1) }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
              <Box sx={iconBox()}>
                <Smartphone sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                  {t.sePay.deviceHeading}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t.sePay.deviceHint}
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={1.25}>
              {[
                {
                  checked: sePayMobileLiteRequireCheckout,
                  onChange: (c: boolean) => void saveSePayMobileCheckoutFlag({ sePayMobileLiteRequireCheckout: c }),
                  title: t.sePay.liteRequireQr,
                  sub: 'Device Management gắn máy',
                },
                {
                  checked: sePayMobileProRequireCheckout,
                  onChange: (c: boolean) => void saveSePayMobileCheckoutFlag({ sePayMobileProRequireCheckout: c }),
                  title: t.sePay.proRequireQr,
                  sub: 'Phiên không gắn máy',
                },
              ].map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    p: 1.25,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0, width: '100%', justifyContent: 'space-between', ml: 0 }}
                    labelPlacement="start"
                    control={
                      <Switch
                        checked={item.checked}
                        onChange={(_, c) => item.onChange(c)}
                        disabled={sePayDeviceFlagsBusy}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ pr: 1, minWidth: 0 }}>
                        <Typography fontWeight={700} sx={{ fontSize: '0.88rem' }}>
                          {item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.sub}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        <Paper elevation={0} sx={{ ...sectionCard, ...userCardEnterSx(2) }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
            <Box sx={iconBox('secondary')}>
              <QrCode2 sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                {t.sePay.createQrTitle}
              </Typography>
              {!isMobile && (
                <Typography variant="caption" color="text.secondary">
                  Tạo QR để khách quét chuyển khoản
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack spacing={1.25}>
            <TextField
              size="small"
              fullWidth
              label={t.sePay.amount}
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value.replace(/\D/g, ''))}
              placeholder="50000"
              inputProps={{ inputMode: 'numeric' }}
            />
            <TextField
              size="small"
              fullWidth
              label={t.sePay.description}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 100))}
              inputProps={{ maxLength: 100 }}
              helperText={`${description.length}/100`}
            />
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={handleSePayCreateCheckout}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <QrCode2 />}
              sx={{ ...pillBtn, py: 1.1 }}
            >
              {loading ? 'Đang tạo…' : 'Tạo QR'}
            </Button>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ ...sectionCard, ...userCardEnterSx(3) }}>
          <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={iconBox()}>
                <History sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                  Giao dịch
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Đồng bộ hoặc làm mới danh sách
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} flexShrink={0}>
              <IconButton
                size="small"
                disabled={loading}
                onClick={async () => {
                  try {
                    setLoading(true);
                    const syncR = await api.syncSePayTransactions();
                    await loadSePayTx();
                    if (syncR.updated > 0) toast.success(syncR.message);
                    else toast.info(syncR.message);
                  } catch (err: unknown) {
                    const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
                    toast.error(msg || 'Đồng bộ thất bại');
                  } finally {
                    setLoading(false);
                  }
                }}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                aria-label="Đồng bộ"
              >
                <Sync fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={loading}
                onClick={() => void loadSePayTx()}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                aria-label="Làm mới"
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {sePayWebhookUrl && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 1.5, borderRadius: 2, py: 0.75 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Webhook chưa nhận thông báo?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                Vào{' '}
                <a href="https://my.sepay.vn/webhooks" target="_blank" rel="noopener noreferrer">
                  my.sepay.vn → WebHooks
                </a>{' '}
                → dán URL webhook → API Key = API Token → sự kiện Tiền vào.
              </Typography>
            </Alert>
          )}

          <SePayTransactionsTable rows={sePayRows} loading={sePayTxLoading} variant="user" maxHeight={420} />
        </Paper>
      </Box>

          {/* Dialog chọn tài khoản ngân hàng + VA từ SePay */}
          <Dialog
            open={sePayBankDialogOpen}
            onClose={() => setSePayBankDialogOpen(false)}
            fullWidth
            maxWidth="sm"
            aria-labelledby="sepay-bank-dialog-title"
            PaperProps={{
              elevation: 0,
              sx: {
                width: '100%',
                maxWidth: 440,
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
              },
            }}
            BackdropProps={{
              sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(15, 23, 42, 0.45)' },
            }}
            TransitionProps={{
              onEntered: async () => {
                setSePayAccountsLoading(true);
                // Reset selection trước khi load
                setSePaySelectedBankId('');
                setSePaySelectedVA('');
                try {
                  const data = await api.getSePayAccountsWithVA();
                  const bas = data.bankAccounts || [];
                  const vas = data.virtualAccounts || [];
                  setSePayAccountsList(bas);
                  setSePayVAList(vas);

                  const savedAccNum = sePayBankInfo?.accountNumber || '';

                  // Tìm VA đã lưu trong danh sách VA
                  const savedVA = vas.find(v => v.account_number === savedAccNum);
                  if (savedVA) {
                    // Lấy bank cha của VA đã lưu
                    const parentBankId = String((savedVA as { _bankAccountId?: string })._bankAccountId || '');
                    setSePaySelectedBankId(parentBankId || (bas[0]?.id ?? ''));
                    setSePaySelectedVA(savedAccNum);
                  } else if (bas.length > 0) {
                    // Chưa có VA lưu → chọn bank đầu tiên, để VA trống (user phải chọn)
                    const firstBankMatch = bas.find(b => b.account_number === savedAccNum);
                    setSePaySelectedBankId(firstBankMatch?.id ?? bas[0].id);
                    setSePaySelectedVA('');
                  }
                } catch {
                  setSePayAccountsList([]);
                  setSePayVAList([]);
                  // Fallback: điền form nhập tay từ thông tin đã lưu
                  setSePayBankForm({
                    bankShortName: sePayBankInfo?.bankShortName || '',
                    accountNumber: sePayBankInfo?.accountNumber || '',
                    accountName: sePayBankInfo?.accountName || '',
                  });
                } finally {
                  setSePayAccountsLoading(false);
                }
              }
            }}
          >
            <DialogTitle component="div" sx={{ p: 0, m: 0 }}>
              <Box
                sx={{
                  px: 2.5,
                  pt: 2.5,
                  pb: 2,
                  background: (t) =>
                    t.palette.mode === 'dark'
                      ? 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)'
                      : 'linear-gradient(145deg, #f8fafc 0%, #eef2ff 55%, #f1f5f9 100%)',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1.75}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      boxShadow: (t) => `0 8px 20px ${t.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(37, 99, 235, 0.35)'}`,
                    }}
                  >
                    <AccountBalance sx={{ fontSize: 28 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography id="sepay-bank-dialog-title" variant="h6" component="h2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                      Chọn tài khoản nhận tiền SePay
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Chọn ngân hàng, sau đó chọn hoặc nhập số tài khoản ảo (VA).
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </DialogTitle>
            <DialogContent
              sx={(theme) => ({
                px: 2.5,
                pb: 1,
                // MUI: DialogTitle + DialogContent → paddingTop 0; chừa khoảng cách với ô Ngân hàng
                '.MuiDialogTitle-root + &': {
                  paddingTop: theme.spacing(3.5),
                },
              })}
            >
              {sePayAccountsLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
                  <CircularProgress size={36} thickness={4} />
                  <Typography variant="body2" color="text.secondary">
                    Đang tải danh sách từ SePay…
                  </Typography>
                </Box>
              ) : sePayAccountsList.length > 0 ? (
                <Stack spacing={2.5}>
                  <FormControl fullWidth size="medium">
                    <InputLabel id="sepay-bank-label">{t.sePay.bank}</InputLabel>
                    <Select
                      labelId="sepay-bank-label"
                      label={t.sePay.bank}
                      value={sePaySelectedBankId}
                      onChange={(e) => {
                        setSePaySelectedBankId(e.target.value);
                        setSePaySelectedVA('');
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: { borderRadius: 2, mt: 1, boxShadow: '0 16px 40px rgba(15,23,42,0.12)' },
                        },
                      }}
                    >
                      {sePayAccountsList.map((acc) => (
                        <MenuItem key={acc.id} value={acc.id} sx={{ py: 1.25, alignItems: 'flex-start' }}>
                          <Box component="span" sx={{ whiteSpace: 'normal' }}>
                            <Typography component="span" fontWeight={700} color="primary.main">
                              {acc.bank_short_name}
                            </Typography>
                            {' — '}
                            <Typography component="span" variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                              {acc.account_number}
                            </Typography>
                            {acc.account_holder_name ? (
                              <Typography component="div" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {acc.account_holder_name}
                              </Typography>
                            ) : null}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {sePaySelectedBankId && (() => {
                    const vaForBank = sePayVAList.filter(
                      v => String((v as { _bankAccountId?: string })._bankAccountId) === sePaySelectedBankId
                    );
                    return vaForBank.length > 0 ? (
                      <FormControl fullWidth size="medium">
                        <InputLabel id="sepay-va-label">{t.sePay.va}</InputLabel>
                        <Select
                          labelId="sepay-va-label"
                          label={t.sePay.va}
                          value={sePaySelectedVA}
                          onChange={(e) => setSePaySelectedVA(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: { borderRadius: 2, mt: 1, boxShadow: '0 16px 40px rgba(15,23,42,0.12)' },
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>— Chưa chọn —</em>
                          </MenuItem>
                          {vaForBank.map((va) => (
                            <MenuItem key={String(va.id)} value={String(va.account_number)} sx={{ py: 1.25, alignItems: 'flex-start' }}>
                              <Box component="span" sx={{ whiteSpace: 'normal' }}>
                                <Typography component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
                                  {va.account_number}
                                </Typography>
                                {va.account_holder_name ? (
                                  <Typography component="div" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                    {va.account_holder_name}
                                  </Typography>
                                ) : null}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        label="Số tài khoản ảo (VA) *"
                        placeholder="VD: 96247QUAN21112002"
                        value={sePaySelectedVA}
                        onChange={(e) => setSePaySelectedVA(e.target.value)}
                        autoComplete="off"
                        helperText="SePay → Ngân hàng → Tài khoản ảo (VA)"
                      />
                    );
                  })()}

                  {sePaySelectedBankId && !sePaySelectedVA && (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 2,
                        bgcolor: 'warning.50',
                        border: '1px solid',
                        borderColor: 'warning.light',
                      }}
                    >
                      <Typography variant="body2" color="warning.dark" fontWeight={500}>
                        Vui lòng chọn hoặc nhập số VA để QR nhận đúng tài khoản.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Không lấy được danh sách từ SePay API. Nhập thủ công bên dưới.
                  </Typography>
                  <TextField
                    fullWidth
                    label="Tên ngân hàng (ngắn)"
                    placeholder="VD: BIDV, MB, VCB…"
                    value={sePayBankForm.bankShortName}
                    onChange={(e) => setSePayBankForm(p => ({ ...p, bankShortName: e.target.value }))}
                    autoComplete="off"
                  />
                  <TextField
                    fullWidth
                    label="Số tài khoản (VA nếu có)"
                    value={sePayBankForm.accountNumber}
                    onChange={(e) => setSePayBankForm(p => ({ ...p, accountNumber: e.target.value }))}
                    autoComplete="off"
                  />
                  <TextField
                    fullWidth
                    label="Chủ tài khoản (tuỳ chọn)"
                    value={sePayBankForm.accountName}
                    onChange={(e) => setSePayBankForm(p => ({ ...p, accountName: e.target.value }))}
                    autoComplete="off"
                  />
                </Stack>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                px: 2.5,
                py: 2,
                gap: 1.25,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'grey.50'),
                justifyContent: 'flex-end',
              }}
            >
              <Button
                onClick={() => setSePayBankDialogOpen(false)}
                disabled={loading}
                color="inherit"
                sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}
              >
                Hủy
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={
                  loading ||
                  sePayAccountsLoading ||
                  (sePayAccountsList.length > 0
                    ? !sePaySelectedBankId || !sePaySelectedVA
                    : !sePayBankForm.bankShortName || !sePayBankForm.accountNumber)
                }
                sx={{ textTransform: 'none', fontWeight: 700, px: 3, borderRadius: 2, boxShadow: 'none' }}
                onClick={async () => {
                  try {
                    setLoading(true);
                    let payload: Parameters<typeof api.putSePayBankInfo>[0];
                    if (sePayAccountsList.length > 0 && sePaySelectedBankId) {
                      const selectedBank = sePayAccountsList.find(b => b.id === sePaySelectedBankId);
                      // Dùng VA làm số tài khoản nếu user chọn VA, ngược lại dùng số tài khoản chính
                      const accountNumber = sePaySelectedVA || selectedBank?.account_number || '';
                      payload = {
                        bankShortName: selectedBank?.bank_short_name || '',
                        accountNumber,
                        accountName: selectedBank?.account_holder_name || '',
                        bankCode: selectedBank?.bank_bin || '',
                      };
                    } else {
                      payload = sePayBankForm;
                    }
                    const r = await api.putSePayBankInfo(payload);
                    setSePayBankInfo(r.bankInfo);
                    setSePayBankDialogOpen(false);
                    toast.success(
                      sePaySelectedVA
                        ? `Đã lưu tài khoản VA: ${sePaySelectedVA}`
                        : 'Đã lưu thông tin ngân hàng.'
                    );
                  } catch (err: unknown) {
                    const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
                    toast.error(msg || 'Lưu thất bại');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Lưu
              </Button>
            </DialogActions>
          </Dialog>

      {/* ── Dialog xác nhận mật khẩu để xem mã SePay ── */}
      <Dialog
        open={revealDialogOpen}
        onClose={() => {
          if (loading) return;
          setRevealDialogOpen(false);
          setSePayPendingPwField(null);
        }}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.15rem' } }}>
          {t.sePay.passwordDialog}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Một lần mật khẩu mở phiên 5 phút để xem <strong>API Token</strong>.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Mật khẩu tài khoản"
            type="password"
            fullWidth
            size="small"
            value={revealPw}
            onChange={(e) => setRevealPw(e.target.value)}
            onKeyDown={(e) =>
              e.key === 'Enter' &&
              (e.preventDefault(), void confirmSePayRevealKeys())
            }
            disabled={loading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setRevealDialogOpen(false);
              setSePayPendingPwField(null);
            }}
            disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            {t.common.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmSePayRevealKeys()}
            disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none', minWidth: 96 }}
          >
            {loading ? <CircularProgress size={16} /> : 'Xem mã'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog hiển thị QR SePay để quét chuyển khoản ── */}
      <Dialog
        open={sePayQrOpen}
        onClose={() => setSePayQrOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{t.sePay.qrDialog}</DialogTitle>
        <DialogContent>
          {sePayQrData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src={sePayQrData.qrUrl}
                alt="QR thanh toán SePay"
                sx={{ width: isMobile ? 240 : 220, height: isMobile ? 240 : 220, objectFit: 'contain', borderRadius: 1 }}
              />
              <Box
                sx={{
                  width: '100%',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  p: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2">
                  <strong>{t.sePay.amount}:</strong> {MONEY_FMT.format(sePayQrData.amount)} VND
                </Typography>
                {sePayQrData.bankShortName && (
                  <Typography variant="body2">
                    <strong>{t.sePay.bank}:</strong> {sePayQrData.bankShortName}
                  </Typography>
                )}
                {sePayQrData.accountNumber && (
                  <Typography variant="body2">
                    <strong>Số TK:</strong> {sePayQrData.accountNumber}
                  </Typography>
                )}
                {sePayQrData.accountName && (
                  <Typography variant="body2">
                    <strong>Chủ TK:</strong> {sePayQrData.accountName}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ wordBreak: 'break-all', mt: 0.5 }}>
                  <strong>Nội dung CK:</strong> {sePayQrData.content}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} color="success" />
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  Đang chờ thanh toán... Dialog sẽ tự đóng khi nhận tiền.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              void loadSePayTx();
              toast.info('Đã làm mới danh sách.', { autoClose: 1500 });
            }}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            Làm mới
          </Button>
          <Button
            variant="contained"
            onClick={() => setSePayQrOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none' }}
          >
            {t.common.close}
          </Button>
        </DialogActions>
      </Dialog>

    </UserPageShell>
  );
};

export default PaymentPayOs;
